/**
 * The session socket.
 *
 * Everything live flows through here: the peer's positions, who is present, roles, and the end
 * of the session. It reconnects on its own, because the native habitat is a beach with one bar
 * of signal and a dropped connection must not end the meeting.
 */

import {
  POSITION,
  parseServerMessage,
  type ClientMessage,
  type ParticipantId,
  type PositionFrame,
  type Role,
  type ServerMessage,
  type SignalKind,
  type TelemetrySummary,
} from '@beeline/shared';

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Why the socket gave up. `ended` and `refused` are final; anything else is retried. */
export type SocketEndReason = 'ended' | 'refused' | 'gone';

export interface SessionSocketOptions {
  code: string;
  participant: ParticipantId;
  token: string;
  onMessage: (message: ServerMessage) => void;
  onStatus?: (status: SocketStatus) => void;
  /** Called once when the socket will not try again. */
  onEnd?: (reason: SocketEndReason) => void;
}

/**
 * Close codes the server uses for application reasons (1000–2999 are reserved by the protocol).
 * These are the ones worth not retrying.
 */
const TERMINAL_CLOSE_CODES = new Map<number, SocketEndReason>([
  [4404, 'gone'],
  [4410, 'ended'],
  [4409, 'refused'],
  [4401, 'refused'],
]);

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;

/**
 * Give up eventually. A refused upgrade arrives as an ordinary close, so without a cap a
 * session that vanished server-side would be retried forever. Roughly two minutes of trying,
 * which comfortably covers walking through a dead spot.
 */
const MAX_RECONNECT_ATTEMPTS = 12;

export class SessionSocket {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: number | null = null;
  private status: SocketStatus = 'closed';
  private stopped = false;
  private lastSentAt = 0;
  /** The newest frame we were asked to send but throttled. Sent when the window opens. */
  private pendingFrame: PositionFrame | null = null;
  private flushTimer: number | null = null;

  constructor(private readonly options: SessionSocketOptions) {}

  get url(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const query = new URLSearchParams({
      p: this.options.participant,
      token: this.options.token,
    });
    return `${protocol}//${window.location.host}/ws/${this.options.code}?${query.toString()}`;
  }

  get currentStatus(): SocketStatus {
    return this.status;
  }

  connect(): void {
    if (this.stopped || this.socket) return;

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.setStatus('open');
      // A reconnect should not lose the position we were holding.
      if (this.pendingFrame) this.flush();
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      const message = parseServerMessage(event.data);
      if (message) this.options.onMessage(message);
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      this.socket = null;
      if (this.stopped) return;

      const terminal = TERMINAL_CLOSE_CODES.get(event.code);
      if (terminal) {
        this.stopped = true;
        this.setStatus('closed');
        this.options.onEnd?.(terminal);
        return;
      }

      this.scheduleReconnect();
    });

    // 'error' is always followed by 'close', so reconnection is handled in one place.
    socket.addEventListener('error', () => {});
  }

  /**
   * Send a position, throttled to at most one per second (`ProductOverview.md` §4.1 protocol
   * rules). A frame arriving inside the window replaces any frame still waiting, so what
   * eventually goes out is always the freshest one.
   */
  sendPosition(frame: PositionFrame): void {
    this.pendingFrame = frame;

    const sinceLast = Date.now() - this.lastSentAt;
    if (sinceLast >= POSITION.minSendIntervalMs) {
      this.flush();
      return;
    }

    if (this.flushTimer === null) {
      this.flushTimer = window.setTimeout(
        () => this.flush(),
        POSITION.minSendIntervalMs - sinceLast,
      );
    }
  }

  /** Claim a role; the peer is assigned the opposite. */
  sendRole(role: Role): void {
    this.send({ type: 'role', role });
  }

  sendName(name: string | null): void {
    this.send({ type: 'name', name });
  }

  sendConverged(): void {
    this.send({ type: 'converged' });
  }

  /** A wave, or one of the four fixed phrases. Never queued: a late gesture is not a gesture. */
  sendSignal(signal: SignalKind): boolean {
    return this.send({ type: 'signal', signal });
  }

  /**
   * Set, move, or clear the room's rally point. Null clears it.
   *
   * Not throttled with the position frames: this is a deliberate act, it happens once in a
   * while, and delaying it by a second would make dropping a pin feel broken.
   */
  sendDestination(
    destination: { lat: number; lng: number; label: string; photo?: string | null } | null,
  ): boolean {
    return this.send({ type: 'destination', destination });
  }

  /** One anonymous summary, at the end. Counts and distances only — never a coordinate. */
  sendTelemetry(summary: TelemetrySummary): boolean {
    return this.send({ type: 'telemetry', summary });
  }

  close(): void {
    this.stopped = true;
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    if (this.flushTimer !== null) window.clearTimeout(this.flushTimer);
    this.retryTimer = null;
    this.flushTimer = null;
    this.socket?.close();
    this.socket = null;
    this.setStatus('closed');
  }

  /* ---------------------------------------------------------------------- */

  private flush(): void {
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const frame = this.pendingFrame;
    if (!frame) return;
    if (!this.send({ type: 'pos', ...frame })) return;

    this.pendingFrame = null;
    this.lastSentAt = Date.now();
  }

  private send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  private scheduleReconnect(): void {
    if (this.attempt >= MAX_RECONNECT_ATTEMPTS) {
      this.stopped = true;
      this.setStatus('closed');
      this.options.onEnd?.('gone');
      return;
    }

    this.setStatus('reconnecting');

    // Exponential backoff with jitter, so two phones that lost the same tunnel do not
    // reconnect in lockstep.
    const backoff = Math.min(RECONNECT_BASE_MS * 2 ** this.attempt, RECONNECT_MAX_MS);
    const delay = backoff * (0.7 + Math.random() * 0.6);
    this.attempt++;

    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }
}
